import {validatePageSettings} from './page-settings.js';
import {validateLegacyDisclaimerIds} from './disclaimers.js';
import {validateMetaobjectHandles} from './metaobject-handles.js';
import {configurationStructureErrors} from './schema-v1.js';

export const SCHEMA_VERSION = 1;
export const MAX_CONFIGURATION_BYTES = 128 * 1024;
export const MAX_COMPONENT_VARIANTS = 128;

export const EMPTY_CONFIGURATION = Object.freeze({
  schemaVersion: SCHEMA_VERSION,
  formOrder: [],
  shopifyOptions: [],
  customOptions: [],
  includedItems: [],
  includedItemOrder: [],
});

export function parseConfiguration(value) {
  const configuration = typeof value === 'string' ? JSON.parse(value) : value;
  const errors = validateConfiguration(configuration);
  if (errors.length) throw new Error(errors.join('\n'));
  return configuration;
}

// Drafts preserve incomplete work. Publish still uses the full validator above.
export function parseDraftConfiguration(value) {
  const configuration = structuredClone(typeof value === 'string' ? JSON.parse(value) : value);
  const structureErrors = configurationStructureErrors(configuration);
  if (structureErrors.length) throw new Error(structureErrors.join('\n'));
  if (!configuration || configuration.schemaVersion !== SCHEMA_VERSION ||
      !['formOrder', 'shopifyOptions', 'customOptions', 'includedItems'].every((key) => Array.isArray(configuration[key]))) {
    throw new Error('Draft configuration has an invalid structure.');
  }
  if (new TextEncoder().encode(JSON.stringify(configuration)).length > MAX_CONFIGURATION_BYTES) throw new Error('Configuration exceeds the size limit.');
  const settingsErrors = [...validatePageSettings(configuration.settings), ...validateLegacyDisclaimerIds(configuration.noticeIds), ...validateMetaobjectHandles(configuration)];
  if (settingsErrors.length) throw new Error(settingsErrors.join('\n'));
  for (const item of configuration.includedItems) {
    if (item.url && !isSafeStorefrontUrl(item.url)) throw new Error('Includes item has an invalid link.');
  }
  const ids = collectComponentVariantIds(configuration);
  if (ids.length > MAX_COMPONENT_VARIANTS || ids.some((id) => !isVariantGid(id))) throw new Error('Draft has invalid component variant references.');
  return pruneComponentOrder(configuration);
}

// Display ordering must not retain components removed from every choice.
// Keep other keys untouched so validation still catches unrelated mistakes.
export function pruneComponentOrder(configuration) {
  const keys = new Set(collectComponentVariantIds(configuration).map((id) => `custom:${id}`));
  if (Array.isArray(configuration.includedItemOrder)) {
    configuration.includedItemOrder = configuration.includedItemOrder.filter((key) => !key.startsWith('custom:') || keys.has(key));
  }
  return configuration;
}

export function prepareGroupConfigurationForSave(configuration, groupId) {
  const prepared = structuredClone(configuration);
  const group = prepared.customOptions.find(({id}) => id === groupId);
  if (!group) throw new Error(`Custom option ${groupId} was not found.`);
  for (const choice of group.choices || []) {
    choice.choiceType = choice.choiceType === 'customerInput' ? 'customerInput' : 'variants';
    if (choice.choiceType === 'customerInput') choice.components = [];
    else choice.fields = [];
  }
  return prepared;
}

export function validateConfiguration(configuration) {
  const structureErrors = configurationStructureErrors(configuration);
  if (structureErrors.length) return structureErrors;
  const errors = [];
  if (!configuration || typeof configuration !== 'object') return ['Configuration must be an object.'];
  if (configuration.schemaVersion !== SCHEMA_VERSION) errors.push(`schemaVersion must be ${SCHEMA_VERSION}.`);
  for (const key of ['formOrder', 'shopifyOptions', 'customOptions', 'includedItems']) {
    if (!Array.isArray(configuration[key])) errors.push(`${key} must be an array.`);
  }
  if (errors.length) return errors;

  errors.push(...validatePageSettings(configuration.settings));
  errors.push(...validateLegacyDisclaimerIds(configuration.noticeIds));
  errors.push(...validateMetaobjectHandles(configuration));
  validateUniqueIds(configuration.shopifyOptions, 'Native option', errors);
  validateUniqueIds(configuration.customOptions, 'Custom option', errors);
  validateUniqueIds(configuration.includedItems, 'Includes item', errors);

  if (configuration.includedItemOrder != null && !Array.isArray(configuration.includedItemOrder)) errors.push('includedItemOrder must be an array.');
  if (Array.isArray(configuration.includedItemOrder) && new Set(configuration.includedItemOrder).size !== configuration.includedItemOrder.length) errors.push('Packing list order contains duplicate items.');

  const nativeIds = new Set(configuration.shopifyOptions.map(({id}) => id));
  const addonIds = new Set(configuration.customOptions.map(({id}) => id));
  const includeIds = new Set(configuration.includedItems.map(({id}) => id));
  const conditionValues = new Map([
    ...configuration.shopifyOptions.map((option) => [`shopifyOption:${option.id}`, new Set(option.optionValueIds || [])]),
    ...configuration.customOptions.map((group) => [`customOption:${group.id}`, new Set((group.choices || []).map(({id}) => id))]),
  ]);
  const propertyNames = new Set();
  const optionValueIds = new Set(configuration.shopifyOptions.flatMap((option) => option.optionValueIds));
  const includedKeys = new Set([...configuration.includedItems.map((item) => `include:${item.id}`), ...collectComponentVariantIds(configuration).map((id) => `custom:${id}`)]);
  for (const key of configuration.includedItemOrder || []) if (!includedKeys.has(key)) errors.push(`Included item order references missing item ${key}.`);
  for (const item of configuration.includedItems) {
    if (item.type === 'shopifyOption' && Array.isArray(item.optionValueIds) && item.optionValueIds.some((id) => !optionValueIds.has(id))) errors.push(`Included item ${item.id} references a missing Shopify option value.`);
    if (!item.label) errors.push(`Includes item ${item.id || '(unknown)'} needs a label.`);
    if (item.type != null && !['base', 'shopifyOption'].includes(item.type)) errors.push(`Includes item ${item.label || item.id} has an invalid type.`);
    if (item.type === 'shopifyOption' && (!Array.isArray(item.optionValueIds) || !item.optionValueIds.length)) errors.push(`Includes item ${item.label || item.id} needs at least one Shopify option value.`);
    if (item.url && !isSafeStorefrontUrl(item.url)) errors.push(`Includes item ${item.label || item.id} has an invalid link.`);
  }
  const orderKeys = new Set();
  for (const item of configuration.formOrder) {
    const key = `${item?.type}:${item?.id || ''}`;
    if (!['productGroup', 'shopifyOption', 'customOption'].includes(item?.type)) errors.push('Form order contains an invalid item type.');
    if (orderKeys.has(key)) errors.push(`Form order contains duplicate item ${key}.`);
    orderKeys.add(key);
    if (item?.type === 'shopifyOption' && !nativeIds.has(item.id)) errors.push(`Form order references missing native option ${item.id}.`);
    if (item?.type === 'customOption' && !addonIds.has(item.id)) errors.push(`Form order references missing custom option ${item.id}.`);
  }
  for (const id of nativeIds) if (!orderKeys.has(`shopifyOption:${id}`)) errors.push(`Form order is missing native option ${id}.`);
  for (const id of addonIds) if (!orderKeys.has(`customOption:${id}`)) errors.push(`Form order is missing custom option ${id}.`);

  for (const option of configuration.shopifyOptions) {
    if (!option.shopifyOptionId) errors.push(`Native option ${option.id || '(unknown)'} needs a Shopify option ID.`);
    if (!Array.isArray(option.optionValueIds)) errors.push(`Native option ${option.id || '(unknown)'} needs option value IDs.`);
    for (const mapping of Object.values(option.values || {})) validateIncludeIds(mapping?.includeItemIds, includeIds, `Native option ${option.id}`, errors);
  }

  for (const group of configuration.customOptions) {
    if (!group.label) errors.push(`Custom option ${group.id || '(unknown)'} needs a label.`);
    if (group.status != null && !['active', 'draft'].includes(group.status)) errors.push(`${group.label || group.id} has an invalid status.`);
    const min = group.minSelections ?? 0;
    const max = group.maxSelections ?? 1;
    if (!Number.isInteger(min) || !Number.isInteger(max) || min < 0 || max < min) errors.push(`${group.label || group.id} has invalid selection limits.`);
    if (!Array.isArray(group.choices) || !group.choices.length) errors.push(`${group.label || group.id} needs at least one choice.`);
    if (max > (group.choices || []).length) errors.push(`${group.label || group.id} maximum exceeds its choice count.`);
    validateUniqueIds(group.choices || [], `Choice in ${group.label || group.id}`, errors);
    validateConditions(group, nativeIds, addonIds, conditionValues, errors);
    for (const choice of group.choices || []) {
      if (choice.choiceType === 'customerInput' && choice.components?.length) errors.push(`Customer input choice ${choice.id} cannot contain components.`);
      if (choice.choiceType === 'variants' && choice.fields?.length) errors.push(`Variant choice ${choice.id} cannot contain customer fields.`);
      if (!choice.label) errors.push(`Choice ${choice.id || '(unknown)'} needs a label.`);
      if (choice.choiceType != null && !['variants', 'customerInput'].includes(choice.choiceType)) errors.push(`Choice ${choice.label || choice.id} has an invalid type.`);
      validateIncludeIds(choice.includeItemIds, includeIds, `Choice ${choice.label || choice.id}`, errors);
      for (const component of choice.components || []) {
        if (!isVariantGid(component.variantId)) errors.push(`Choice ${choice.label || choice.id} has an invalid variant ID.`);
        if (component.itemLabel != null && !String(component.itemLabel).trim()) errors.push(`Choice ${choice.label || choice.id} has a variant item without an Item label.`);
        if (!Number.isInteger(component.quantity) || component.quantity < 1) errors.push(`Choice ${choice.label || choice.id} has an invalid component quantity.`);
      }
      validateUniqueIds(choice.fields || [], `Customer field in ${choice.label || choice.id}`, errors);
      for (const field of choice.fields || []) {
        if (!field.label || !field.propertyName) errors.push(`Customer field ${field.id || '(unknown)'} needs a label and line-item property name.`);
        if (field.type !== 'text') errors.push(`Customer field ${field.id || '(unknown)'} must use the text type.`);
        const propertyName = String(field.propertyName || '').trim().toLowerCase();
        if (propertyName && propertyNames.has(propertyName)) errors.push(`Customer line-item property names must be unique; ${field.propertyName} is used more than once.`);
        if (propertyName) propertyNames.add(propertyName);
      }
    }
  }

  if (hasConditionCycle(configuration.customOptions)) errors.push('Custom option conditions contain a circular dependency.');
  if (new TextEncoder().encode(JSON.stringify(configuration)).length > MAX_CONFIGURATION_BYTES) errors.push('Configuration exceeds Shopify’s 128 KB JSON metafield limit.');
  if (collectComponentVariantIds(configuration).length > MAX_COMPONENT_VARIANTS) errors.push('Configuration exceeds Shopify’s 128 component variant reference limit.');
  return errors;
}

export function collectComponentVariantIds(configuration) {
  return [...new Set((configuration.customOptions || []).flatMap((group) => group.choices || []).flatMap((choice) => choice.components || []).map(({variantId}) => variantId).filter(Boolean))];
}

export function activeAddonGroups(configuration, selections = {}) {
  return (configuration.customOptions || []).filter((group) => group.status !== 'draft' && conditionsMatch(group.conditions, selections));
}

export function evaluateConfiguration(configuration, selections = {}, inputs = {}) {
  const errors = [];
  const components = new Map();
  const activeGroups = activeAddonGroups(configuration, selections);
  for (const group of activeGroups) {
    const selectedIds = Array.isArray(selections[group.id]) ? selections[group.id] : [];
    const selected = (group.choices || []).filter(({id}) => selectedIds.includes(id));
    if (selected.length < group.minSelections || selected.length > group.maxSelections) errors.push(`${group.label} requires ${selectionMessage(group.minSelections, group.maxSelections)}.`);
    for (const choice of selected) {
      for (const component of choice.components || []) components.set(component.variantId, (components.get(component.variantId) || 0) + component.quantity);
      for (const field of choice.fields || []) {
        if (field.required && !String(inputs[field.id] || '').trim()) errors.push(`${field.label} is required.`);
      }
    }
  }
  return {valid: errors.length === 0, errors, activeGroupIds: activeGroups.map(({id}) => id), components: [...components].map(([variantId, quantity]) => ({variantId, quantity}))};
}

export function createId(prefix, existing = []) {
  let id;
  do id = `${prefix}_${crypto.randomUUID()}`; while (existing.includes(id));
  return id;
}

function validateUniqueIds(items, label, errors) {
  const ids = new Set();
  for (const item of items) {
    if (!item?.id || ids.has(item.id)) errors.push(`${label} IDs must be present and unique.`);
    ids.add(item?.id);
  }
}

function validateIncludeIds(ids = [], knownIds, path, errors) {
  if (!Array.isArray(ids)) { errors.push(`${path} Includes mappings must be an array.`); return; }
  for (const id of ids) if (!knownIds.has(id)) errors.push(`${path} references missing Includes item ${id}.`);
}

function validateConditions(group, nativeIds, addonIds, conditionValues, errors) {
  const conditions = group.conditions || {mode: 'all', rules: []};
  if (!['all', 'any'].includes(conditions.mode)) errors.push(`${group.label || group.id} has an invalid condition mode.`);
  for (const rule of conditions.rules || []) {
    if (!['matchesAny', 'excludesAny', 'hasSelectedValue'].includes(rule.operator)) errors.push(`${group.label || group.id} has an invalid condition operator.`);
    if (!Array.isArray(rule.selectedIds) || (rule.operator !== 'hasSelectedValue' && !rule.selectedIds.length)) errors.push(`${group.label || group.id} has an empty condition.`);
    if (rule.source === 'shopifyOption' && !nativeIds.has(rule.sourceId)) errors.push(`${group.label || group.id} references a missing native option condition.`);
    else if (rule.source === 'customOption' && !addonIds.has(rule.sourceId)) errors.push(`${group.label || group.id} references a missing custom-option condition.`);
    else if (!['shopifyOption', 'customOption'].includes(rule.source)) errors.push(`${group.label || group.id} has an invalid condition source.`);
    const allowed = conditionValues.get(`${rule.source}:${rule.sourceId}`);
    if (allowed && (rule.selectedIds || []).some((id) => !allowed.has(id))) errors.push(`${group.label || group.id} references a missing condition value.`);
  }
}

function conditionsMatch(conditions, selections) {
  const rules = conditions?.rules || [];
  if (!rules.length) return true;
  const results = rules.map((rule) => {
    const selected = Array.isArray(selections[rule.sourceId]) ? selections[rule.sourceId] : [];
    if (rule.operator === 'hasSelectedValue') return selected.length > 0;
    const intersects = rule.selectedIds.some((id) => selected.includes(id));
    return rule.operator === 'excludesAny' ? !intersects : intersects;
  });
  return conditions.mode === 'any' ? results.some(Boolean) : results.every(Boolean);
}

function hasConditionCycle(groups) {
  const dependencies = new Map(groups.map((group) => [group.id, new Set((group.conditions?.rules || []).filter(({source}) => source === 'customOption').map(({sourceId}) => sourceId))]));
  const visiting = new Set();
  const visited = new Set();
  const visit = (id) => {
    if (visiting.has(id)) return true;
    if (visited.has(id)) return false;
    visiting.add(id);
    for (const dependency of dependencies.get(id) || []) if (visit(dependency)) return true;
    visiting.delete(id); visited.add(id); return false;
  };
  return [...dependencies.keys()].some(visit);
}

function isVariantGid(value) {
  return typeof value === 'string' && /^gid:\/\/shopify\/ProductVariant\/\d+$/.test(value);
}

function isSafeStorefrontUrl(value) {
  return /^\/(?!\/)/.test(value) || /^https?:\/\//i.test(value);
}

function selectionMessage(min, max) {
  if (min === max) return min === 1 ? 'one selection' : `${min} selections`;
  if (min === 0) return `no more than ${max} selection${max === 1 ? '' : 's'}`;
  return `${min} to ${max} selections`;
}
